import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNavigate, Link } from 'react-router-dom';
import { Upload as UploadIcon, File, Crown, Plus, X, Loader2, Settings, ChevronDown, ChevronUp, ArrowLeft } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import BreadcrumbSchema from '../components/BreadcrumbSchema';
import { toast } from 'react-toastify';

import { AnalysisRequest } from '../types';
import { formatFileSize, userApi, videoApi } from '../services/api';
import { useChunkedUpload } from '../hooks/useChunkedUpload';
import { useUpload } from '../contexts/UploadContext';
import { useAuth } from '../contexts/AuthContext';
import { useUserPlan } from '../hooks/useUserPlan';
import { useKeepAlive } from '../hooks/useKeepAlive';
import { useAnalytics, useUploadTracking } from '../hooks/useAnalytics';
import AuthPrompt from '../components/AuthPrompt';
import UrlImportCard from '../components/UrlImportCard';
import WatermarkWarning from '../components/WatermarkWarning';
import SubscriptionModal from '../components/SubscriptionModal';
import GuestLimitReached from '../components/GuestLimitReached';
import ExitIntentModal from '../components/ExitIntentModal';
import { useExitIntent } from '../hooks/useExitIntent';
import UsageUpgradeCard from '../components/UsageUpgradeCard';

const Upload: React.FC = () => {
  const navigate = useNavigate();
  const { 
    currentUser, 
    refreshUserProfile, 
    canUseGuestMode, 
    enableGuestMode, 
    markGuestModeUsed 
  } = useAuth();
  const { planData, isPro } = useUserPlan();
  const { warmupBackend } = useKeepAlive();
  const { trackUploadStarted, trackUploadCompleted, trackUploadFailed, trackProcessingStarted } = useUploadTracking();
  const { trackUpgradePromptShown, trackUpgradePromptClicked } = useAnalytics();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [, setAnalyzing] = useState(false);
  const [activeUploads, setActiveUploads] = useState<Set<string>>(new Set()); // Track active uploads by file name+size
  const [showSettings, setShowSettings] = useState(true);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showGuestLimitModal, setShowGuestLimitModal] = useState(false);
  const [uploadCompleted, setUploadCompleted] = useState(false);
  const usageCardShownRef = useRef(false);

  const monthlyLimit = planData?.videos_limit || 0;
  const videosUsed = planData?.videos_count || 0;
  const showUsageCard = !isPro && monthlyLimit > 0 && !!currentUser;
  
  // Exit intent for guests
  const { showExitIntent, dismissExitIntent } = useExitIntent({
    enabled: !currentUser && !uploadCompleted,
    sensitivity: 20
  });

  // IMPROVED: Clip generation settings optimized for better results
  const [analysisSettings, setAnalysisSettings] = useState<Omit<AnalysisRequest, 'video_id'>>({
    sensitivity: 0.5,  // Increased from 0.4 - better balance for most content
    min_clip_duration: 12,  // Reduced from 15 - allows shorter punchy clips
    max_clip_duration: 60,
    padding_before: 3,
    padding_after: 3,
    enable_audio_analysis: true,
    enable_face_detection: true,
    enable_emotion_detection: true,
    audio_threshold: 0.9  // Reduced from 1.0 - more sensitive to audio spikes
  });

  // Video conversion settings
  const [conversionSettings, setConversionSettings] = useState({
    convertToVertical: false
  });

  // Track selected preset for visual feedback
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [isUrlImporting, setIsUrlImporting] = useState(false);

  // Memoize preset configurations to prevent re-creation
  const presets = useMemo(() => ({
    'short': {
      name: 'Short & Sweet',
      description: '10-30s clips, high sensitivity',
      subtitle: 'Perfect for social media',
      color: 'from-red-500 to-red-600',
      textColor: 'text-red-600',
      settings: {
        sensitivity: 0.7,
        min_clip_duration: 10,
        max_clip_duration: 30,
        padding_before: 2,
        padding_after: 2
      }
    },
    'balanced': {
      name: 'Balanced',
      description: '15-60s clips, moderate sensitivity',
      subtitle: 'Great for most content',
      color: 'from-gray-600 to-gray-700',
      textColor: 'text-gray-700',
      settings: {
        sensitivity: 0.5,
        min_clip_duration: 15,
        max_clip_duration: 60,
        padding_before: 3,
        padding_after: 3
      }
    },
    'long': {
      name: 'Long Form',
      description: '20-90s clips, conservative',
      subtitle: 'Best for detailed moments',
      color: 'from-gray-900 to-black',
      textColor: 'text-gray-800',
      settings: {
        sensitivity: 0.3,
        min_clip_duration: 20,
        max_clip_duration: 90,
        padding_before: 5,
        padding_after: 5
      }
    }
  }), []);

  // Memoize the getCurrentPreset function to prevent re-creation
  const getCurrentPreset = useCallback(() => {
    for (const [key, preset] of Object.entries(presets)) {
      const settings = preset.settings;
      if (
        analysisSettings.sensitivity === settings.sensitivity &&
        analysisSettings.min_clip_duration === settings.min_clip_duration &&
        analysisSettings.max_clip_duration === settings.max_clip_duration &&
        analysisSettings.padding_before === settings.padding_before &&
        analysisSettings.padding_after === settings.padding_after
      ) {
        return key;
      }
    }
    return null;
  }, [analysisSettings, presets]);

  // Memoize the applyPreset function
  const applyPreset = useCallback((presetKey: string) => {
    const preset = presets[presetKey as keyof typeof presets];
    if (preset) {
      setAnalysisSettings(prev => ({
        ...prev,
        ...preset.settings
      }));
      setSelectedPreset(presetKey);
    }
  }, [presets]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (showUsageCard && !usageCardShownRef.current) {
      trackUpgradePromptShown('upload_usage_card', planData?.plan || 'free');
      usageCardShownRef.current = true;
    } else if (!showUsageCard) {
      usageCardShownRef.current = false;
    }
  }, [showUsageCard, trackUpgradePromptShown, planData]);

  const handleUploadUsageUpgradeClick = useCallback(() => {
    trackUpgradePromptClicked('upload_usage_card', 'pro');
    setShowSubscriptionModal(true);
  }, [trackUpgradePromptClicked]);

  // Update selected preset when settings change - but only when actually needed
  useEffect(() => {
    const currentPreset = getCurrentPreset();
    if (currentPreset !== selectedPreset) {
      setSelectedPreset(currentPreset);
    }
  }, [getCurrentPreset, selectedPreset]);

  // Use the chunked upload hook for individual files
  const { upload } = useChunkedUpload();

  // Use upload context for background tracking
  const { addTask, updateTask, tasks } = useUpload();

  // Memoize the onDrop callback to prevent re-creation on every render
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    // Prevent multiple drops during upload
    if (isUploading) {
      toast.warning('Please wait for current uploads to complete');
      return;
    }
    
    // Define file size limits based on user plan
    const maxFileSize = isPro ? 2 * 1024 * 1024 * 1024 : 500 * 1024 * 1024; // 2GB for Pro, 500MB for Free
    
    // Check file sizes first
    const oversizedFiles = acceptedFiles.filter(file => file.size > maxFileSize);
    if (oversizedFiles.length > 0) {
      const maxSizeText = isPro ? '2GB' : '500MB';
      toast.error(`File size limit exceeded. Maximum allowed: ${maxSizeText}. Please compress your video or upgrade your plan.`);
      return;
    }
    
    // For pro users, allow multiple files
    if (isPro) {
      const newFiles = acceptedFiles.slice(0, 10 - selectedFiles.length);
      
      // Filter out files that are already selected (by name and size)
      const filteredFiles = newFiles.filter(newFile => 
        !selectedFiles.some(existingFile => 
          existingFile.name === newFile.name && existingFile.size === newFile.size
        )
      );
      
      if (filteredFiles.length !== newFiles.length) {
        toast.warning('Some files were already selected and skipped');
      }
      
      // Check quota for each file (skip for guest users)
      for (const file of filteredFiles) {
        if (planData && currentUser) {
          try {
            const canUpload = await userApi.canUpload(file.size);
            if (!canUpload.allowed) {
              toast.error(`Cannot upload ${file.name}: ${canUpload.reason || 'Quota limit exceeded'}`);
              continue;
            }
          } catch (error: any) {
            console.error('Quota check failed:', error);
            if (error?.response?.status === 401) {
              toast.warning('Please log in to check quota limits');
              return;
            }
          }
        }
      }
      
      setSelectedFiles(prev => [...prev, ...filteredFiles]);
      
      if (filteredFiles.length > 0) {
        toast.success(`Added ${filteredFiles.length} file${filteredFiles.length > 1 ? 's' : ''} to upload queue`);
      }
    } else {
      // Free users - single file only
      const file = acceptedFiles[0];
      if (file) {
        // Check if the same file is already selected
        if (selectedFiles.some(existingFile => 
          existingFile.name === file.name && existingFile.size === file.size
        )) {
          toast.warning('This file is already selected');
          return;
        }
        
        if (planData && currentUser) {
          try {
            const canUpload = await userApi.canUpload(file.size);
            if (!canUpload.allowed) {
              toast.error(canUpload.reason || 'Upload not allowed due to quota limits');
              return;
            }
          } catch (error: any) {
            console.error('Quota check failed:', error);
            if (error?.response?.status === 401) {
              toast.warning('Please log in to check quota limits');
              return;
            }
          }
        }
        setSelectedFiles([file]);
      }
    }
  }, [planData, isPro, selectedFiles, isUploading, currentUser]);

  // Memoize the dropzone configuration
  const dropzoneConfig = useMemo(() => ({
    onDrop,
    accept: {
                  'video/*': ['.mp4', '.avi', '.mkv', '.mov', '.wmv', '.flv', '.webm']
    },
    multiple: isPro,
    maxSize: isPro ? 2 * 1024 * 1024 * 1024 : 500 * 1024 * 1024, // 2GB for Pro, 500MB for Free
    disabled: isUploading || (!currentUser && !canUseGuestMode)
  }), [onDrop, isPro, isUploading, currentUser, canUseGuestMode]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone(dropzoneConfig);

  // Memoize the removeFile callback
  const removeFile = useCallback((index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Memoize the resetUpload callback
  const resetUpload = useCallback(() => {
    setSelectedFiles([]);
    setAnalyzing(false);
    setShowSettings(true);
    // Reset analysis settings to defaults
    setAnalysisSettings({
      sensitivity: 0.3,
      min_clip_duration: 15,
      max_clip_duration: 60,
      padding_before: 3,
      padding_after: 3,
      enable_audio_analysis: true,
      enable_face_detection: true,
      enable_emotion_detection: true,
      audio_threshold: 1.0
    });
    // Reset conversion settings to defaults
    setConversionSettings({
      convertToVertical: false
    });
  }, []);

  const uploadSingleFile = async (file: File): Promise<string> => {
    // Create unique identifier for this file
    const fileKey = `${file.name}_${file.size}`;
    // console.log(`🚀 [Upload.tsx] Starting upload for: ${fileKey}`);
    
    // Check if this file is already being uploaded in activeUploads
    if (activeUploads.has(fileKey)) {
      // const message = `${file.name} is already being uploaded`;
      // console.log(`❌ [Upload.tsx] ${message}`);
      throw new Error('This file is already being uploaded');
    }
    
    // Also check if there's already an active task for this file
    const existingActiveTask = tasks.find(task => 
      `${task.fileName}_${task.fileSize}` === fileKey && 
      (task.status === 'uploading' || task.status === 'analyzing')
    );
    
    if (existingActiveTask) {
      // const message = `${file.name} is already being processed`;
      // console.log(  `❌ [Upload.tsx] ${message}`);
      throw new Error('This file is already being uploaded');
    }
    
    // Add to active uploads first
    setActiveUploads(prev => {
      const newSet = new Set(prev).add(fileKey);
      // console.log(`📋 [Upload.tsx] Active uploads:`, Array.from(newSet));
      return newSet;
    });
    
    // Track upload started
    trackUploadStarted(file.name, file.size);
    
    // Add task to background tracker (this should not create duplicates now)
    const taskId = addTask(file);
    // console.log(`📋 [Upload.tsx] Created task: ${taskId}`);
    
    try {
      // Update task status
      // console.log(`🔄 [Upload.tsx] Updating task ${taskId} to uploading status`);
      updateTask(taskId, { status: 'uploading', uploadStatus: 'Starting upload...' });
      
      // Use the existing chunked upload hook with progress callback
      // console.log(`🚀 [Upload.tsx] Starting chunked upload for ${file.name}`);
      const resultVideoId = await upload(file, (progress, status) => {
        // Update the task with real-time progress from the chunked upload
        // Show actual upload progress: 0-100% for upload phase
        let taskProgress;
        
        if (progress >= 100) {
          // Upload is truly complete, show 100% and prepare for analysis transition
          taskProgress = 100;
          // console.log(`📊 [Upload.tsx] Upload complete: ${progress}% → ${taskProgress}% (${status})`);
        } else {
          // Show actual chunk upload progress (0-100%)
          taskProgress = Math.min(Math.max(progress, 0), 100);
          // console.log(`📊 [Upload.tsx] Progress callback: ${progress}% → ${taskProgress}% (${status})`);
        }
        
        updateTask(taskId, { 
          progress: taskProgress,
          uploadStatus: status
        });
      });
      
      // Track upload completion
      trackUploadCompleted(file.name);
      
      // console.log(`✅ [Upload.tsx] Upload completed, videoId: ${resultVideoId}`);
      
      // Update task with video ID and transition to analysis
      // console.log(`🔄 [Upload.tsx] Transitioning task ${taskId} to analysis phase`);
      updateTask(taskId, { 
        status: 'analyzing',
        progress: 0, // Reset progress to 0 for analysis phase
        videoId: resultVideoId,
        uploadStatus: 'Upload completed successfully!',
        processingStatus: 'Preparing for analysis...',
        canCancel: true
      });
      
      // Add a 5-second buffer before starting analysis to ensure video is fully saved
      setTimeout(async () => {
        try {
          updateTask(taskId, { 
            processingStatus: 'Starting video analysis...',
            progress: 5
          });
          
          // Track processing started
          trackProcessingStarted(resultVideoId);
          
          // console.log(`🔬 [Upload.tsx] Starting analysis for video ${resultVideoId} with settings:`, analysisSettings);
          await videoApi.analyzeVideo({
            video_id: resultVideoId,
            ...analysisSettings,
            ...conversionSettings
          }, { silent: true });
          // console.log(`✅ [Upload.tsx] Analysis started successfully for video ${resultVideoId}`);
        } catch (analysisError: any) {
          console.error(`❌ [Upload.tsx] Failed to start analysis for video ${resultVideoId}:`, analysisError);
          
          // Check if it's already being processed (400 error)
          if (analysisError?.response?.status === 400) {
            updateTask(taskId, { 
              processingStatus: 'Analysis already in progress...',
              progress: 10
            });
          } else {
            updateTask(taskId, { 
              status: 'error',
              error: `Upload succeeded but analysis failed: ${analysisError.message}`,
              uploadStatus: 'Analysis failed to start',
              canCancel: false
            });
          }
        }
      }, 5000); // 5-second buffer
      
      return resultVideoId;
    } catch (error: any) {
      console.error(`❌ [Upload.tsx] Upload error for ${file.name}:`, error);
      
      // Track upload failure
      trackUploadFailed(file.name, error.message || 'Upload failed');
      
      // Check if it's a guest limit error (429 status)
      if (error?.response?.status === 429 && !currentUser) {
        setShowGuestLimitModal(true);
      }
      
      // Only update task if it's not a duplication error
      if (!error.message.includes('already being')) {
        // console.log(`🔄 [Upload.tsx] Updating task ${taskId} to error status`);
        updateTask(taskId, { 
          status: 'error', 
          error: error.message || 'Upload failed',
          uploadStatus: 'Upload failed',
          canCancel: false
        });
      }
      
      throw error;
    } finally {
      // Remove from active uploads
      setActiveUploads(prev => {
        const newSet = new Set(prev);
        newSet.delete(fileKey);
        // console.log(`📋 [Upload.tsx] Removed from active uploads, remaining:`, Array.from(newSet));
        return newSet;
      });
    }
  };

  const handleUploadAll = async () => {
    if (selectedFiles.length === 0) return;
    
    // Check authentication - allow guest mode for first-time users
    if (!currentUser && !canUseGuestMode) {
      setShowAuthPrompt(true);
      return;
    }
    
    // Prevent multiple upload attempts
    if (isUploading) {
      toast.warning('Upload is already in progress');
      return;
    }

    // Additional check for active uploads
    if (activeUploads.size > 0) {
      toast.warning('Some files are already being uploaded');
      return;
    }

    setIsUploading(true);
    
    // Enable guest mode if user is not authenticated
    if (!currentUser && canUseGuestMode) {
      enableGuestMode();
    }
    
    // Warmup backend before starting upload
    toast.info('Preparing backend for upload...');
    await warmupBackend();
    
    try {
      if (isPro) {
        // For pro users, upload all files in parallel
        toast.info(`Starting upload of ${selectedFiles.length} files...`);
        
        // Filter out files that might already be uploading
        const filesToUpload = selectedFiles.filter(file => {
          const fileKey = `${file.name}_${file.size}`;
          return !activeUploads.has(fileKey);
        });
        
        if (filesToUpload.length !== selectedFiles.length) {
          toast.warning('Some files are already being uploaded and were skipped');
        }
        
        if (filesToUpload.length === 0) {
          toast.info('All selected files are already being uploaded');
          return;
        }
        
        const uploadPromises = filesToUpload.map(file => uploadSingleFile(file));
        const results = await Promise.allSettled(uploadPromises);
        
        const successful = results.filter(r => r.status === 'fulfilled').length;
        const failed = results.filter(r => r.status === 'rejected').length;
        
        if (successful > 0) {
          toast.success(`${successful} file${successful > 1 ? 's' : ''} uploaded successfully!`);
          setUploadCompleted(true);
          
          // Show guest limit modal after successful upload for guests
          if (!currentUser) {
            setTimeout(() => {
              setShowGuestLimitModal(true);
            }, 2000); // Show after 2 seconds
          }
        }
        if (failed > 0) {
          // const failedResults = results.filter(r => r.status === 'rejected') as PromiseRejectedResult[];
          // console.log('Failed uploads:', failedResults.map(r => r.reason));
          toast.error(`${failed} file${failed > 1 ? 's' : ''} failed to upload`);
        }
        
        // Clear selected files and navigate appropriately
        // setSelectedFiles([]); // Remove this to prevent showing reset state
        
        // Mark guest mode as used if this was a guest upload
        if (!currentUser && successful > 0) {
          markGuestModeUsed();
        }
        
        // Refresh user profile to update video count after successful uploads (for authenticated users)
        if (successful > 0 && currentUser) {
          refreshUserProfile();
        }
        
        // For single successful upload, go to video page; for multiple, go to dashboard
        if (successful === 1 && failed === 0) {
          const successfulResult = results.find(r => r.status === 'fulfilled') as PromiseFulfilledResult<string>;
          if (successfulResult?.value) {
            // Delay navigation until the video is saved and thumbnail is ready
            await waitForUploadReadyAndNavigate(successfulResult.value);
          } else {
            navigate('/'); // Navigate immediately
          }
        } else {
          navigate('/'); // Navigate immediately
        }
        
      } else {
        // For free users, upload single file
        const file = selectedFiles[0];
        const fileKey = `${file.name}_${file.size}`;
        
        if (activeUploads.has(fileKey)) {
          toast.warning('This file is already being uploaded');
          return;
        }
        
        const videoId = await uploadSingleFile(file);
        toast.success('Video uploaded successfully!');
        setUploadCompleted(true);
        
        // Mark guest mode as used if this was a guest upload
        if (!currentUser) {
          markGuestModeUsed();
          // Show guest limit modal after successful upload
          setTimeout(() => {
            setShowGuestLimitModal(true);
          }, 2000); // Show after 2 seconds
        }
        
        // Refresh user profile to update video count (for authenticated users)
        if (currentUser) {
          refreshUserProfile();
        }
        
        // Delay navigation until the video is saved and thumbnail is ready
        await waitForUploadReadyAndNavigate(videoId);
      }
      
    } catch (error: any) {
      console.error('Upload error:', error);
      if (!error.message.includes('already being')) {
        toast.error('Some uploads failed. Check the upload widget for details.');
      }
    } finally {
      setIsUploading(false);
    }
  };

  // Helper: wait until uploaded video has at least 'uploaded' with thumbnail (or 'analyzed'), then navigate
  const waitForUploadReadyAndNavigate = async (videoId: string) => {
    const start = Date.now();
    const maxWaitMs = 300000; // 5 minutes for large uploads
    const pollIntervalMs = 3000;
    
    while (Date.now() - start < maxWaitMs) {
      try {
        const v = await videoApi.getVideo(videoId);
        const hasThumb = !!v?.thumbnail_url;
        const hasValidFilename = v?.metadata?.filename && 
          !v.metadata.filename.startsWith("import_pending_") && 
          v.metadata.filename !== "processing.mp4";
        const hasDuration = v?.metadata?.duration && v.metadata.duration > 0;
        
        // Navigate only when we have essential metadata ready to avoid placeholder state
        if ((v?.status === 'uploaded' && hasThumb && hasValidFilename && hasDuration) || 
            v?.status === 'analyzed' || 
            (v?.status === 'processing' && hasThumb && hasValidFilename && hasDuration)) {
          navigate(`/videos/${videoId}`);
          return;
        }
      } catch (e) {
        // ignore transient errors
      }
      await new Promise(res => setTimeout(res, pollIntervalMs));
    }
    // Fallback timeout: do not navigate to prevent broken details view
    toast.info('Upload still finalizing... We will open the video once metadata is ready.');
  };

  return (
    <>
      <SEOHead 
        title="Upload Video - ClipThis"
        description="Upload videos or import from YouTube, Twitch, and more to create viral clips with AI."
      />
      <BreadcrumbSchema items={[
        { name: 'Upload', path: '/upload' }
      ]} />
      <div className="relative overflow-x-hidden" style={{ minHeight: 'calc(100vh - 64px)' }}>
      {/* Subtle animated background */}
      <div className="absolute inset-0 dot-pattern-animated opacity-30 pointer-events-none" />
      
      <div className="px-6 sm:px-8 relative z-10 animate-fade-up">
        {/* Back Button */}
        <div className="py-4">
          <Link 
            to={currentUser ? "/dashboard" : "/"}
            className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 transition-colors mb-8"
          >
            <ArrowLeft size={20} />
            <span>Back to {currentUser ? "Dashboard" : "Home"}</span>
          </Link>
        </div>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-red-600 rounded-xl flex items-center justify-center">
              <UploadIcon className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-5xl font-display font-bold tracking-tight mb-4">Clipper</h1>
          <p className="text-xl text-primary-600 max-w-3xl mx-auto">
            Upload videos/import from YouTube, Twitch, and Kick to create viral clips with AI
          </p>
        </div>
        
        {showUsageCard && (
          <div className="max-w-4xl mx-auto mb-10">
            <UsageUpgradeCard
              videosUsed={videosUsed}
              monthlyLimit={monthlyLimit}
              onUpgrade={handleUploadUsageUpgradeClick}
            />
          </div>
        )}

        <div>
          
          {/* Account Required Message */}
          {!currentUser && (
            <div className="bg-white border-2 border-red-500 rounded-xl p-6 mb-8 shadow-lg">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-2xl">🔒</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="text-black font-bold text-lg">Account Required</h3>
                    <span className="bg-red-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">NEW POLICY</span>
                  </div>
                  <p className="text-gray-800 text-sm mb-3">
                    Due to <strong className="text-red-600">excessive abuse from anonymous users</strong>, we now require a free account to upload and process videos. This helps us keep the service sustainable for everyone.
                  </p>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3">
                    <p className="text-black text-xs font-medium mb-2">✨ Free Account Benefits:</p>
                    <ul className="text-gray-800 text-xs space-y-1">
                      <li className="flex items-start">
                        <span className="text-red-600 font-bold mr-2">✓</span>
                        <span><strong>1 video (lifetime)</strong> - test our AI for free</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-red-600 font-bold mr-2">✓</span>
                        <span><strong>Permanent clip storage</strong> - keep your clips forever</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-red-600 font-bold mr-2">✓</span>
                        <span><strong>No credit card required</strong> - sign up in 30 seconds</span>
                      </li>
                    </ul>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Link
                      to="/signup"
                      className="flex-1 bg-red-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-red-700 hover:shadow-lg transition-all text-center"
                    >
                      Create Free Account
                    </Link>
                    <Link
                      to="/login"
                      className="flex-1 bg-white border-2 border-black text-black px-6 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-all text-center"
                    >
                      Sign In
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* URL Import Section */}
        <div className="mb-16">
          <UrlImportCard 
            onImportStart={(_url, _platform) => {
              setIsUrlImporting(true);
              // Refresh user profile after URL import starts
              refreshUserProfile();
              // Don't navigate to dashboard - let UrlImportCard handle direct video navigation
            }}
            onImportComplete={() => {
              setIsUrlImporting(false);
            }}
            onImportError={() => {
              setIsUrlImporting(false);
            }}
            className="max-w-4xl mx-auto"
            isPro={isPro}
          />
          
          {!isPro && (
            <div className="max-w-4xl mx-auto mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div 
                className="flex items-center space-x-2 text-gray-700 cursor-pointer hover:text-red-600 transition-colors"
                onClick={() => navigate('/pricing')}
              >
                <Crown className="w-5 h-5 text-red-600" />
                <span className="font-semibold">Pro Features:</span>
              </div>
              <p className="text-gray-600 mt-1">
                YouTube imports are free for all users! <span 
                  className="text-red-600 font-semibold cursor-pointer hover:underline"
                  onClick={() => navigate('/pricing')}
                >
                  Upgrade to Pro
                </span> for Twitch and Kick imports.
              </p>
            </div>
          )}
        </div>

        {/* Divider */}
        {isPro && (
          <div className="flex items-center my-12">
            <div className="flex-1 border-t border-gray-300"></div>
            <div className="px-6 text-gray-500 font-medium">OR</div>
            <div className="flex-1 border-t border-gray-300"></div>
          </div>
        )}

        {/* Upload Section */}
        {!selectedFiles.length ? (
          <div>
            <div className="card">
              <div className="mb-8">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-2xl font-display font-semibold">Upload Video{isPro ? 's' : ''}</h2>
                  {isPro && (
                    <div className="flex items-center space-x-2 bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                      <Crown className="h-4 w-4" />
                      <span>Pro</span>
                    </div>
                  )}
                </div>
                <p className="text-primary-600">
                  {isPro 
                    ? `Upload up to 10 video files at once for batch processing`
                    : 'Choose a video file from your device to analyze'
                  }
                </p>
              </div>
              
              <div
                {...getRootProps()}
                className={`relative border-2 border-dashed rounded-2xl p-12 text-center transition-all duration-300 ${
                  isUrlImporting
                    ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
                    : (!currentUser && !canUseGuestMode)
                    ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
                    : isDragActive
                    ? 'border-black bg-primary-50 scale-[0.98] cursor-pointer'
                    : 'border-primary-200 hover:border-primary-300 hover:bg-primary-50 cursor-pointer'
                }`}
              >
                <input {...getInputProps()} disabled={isUrlImporting || (!currentUser && !canUseGuestMode)} />
                <UploadIcon className={`h-16 w-16 mx-auto mb-6 transition-all duration-300 ${
                  isUrlImporting || (!currentUser && !canUseGuestMode) ? 'text-gray-400' : isDragActive ? 'text-black animate-pulse' : 'text-primary-400'
                }`} />
                
                {isUrlImporting ? (
                  <div>
                    <p className="text-xl font-medium mb-2 text-gray-500">
                      URL Import in Progress
                    </p>
                    <p className="text-sm text-gray-500 mb-4">
                      Please wait for the current import to complete
                    </p>
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                ) : (!currentUser && !canUseGuestMode) ? (
                  <div>
                    <p className="text-xl font-medium mb-2 text-gray-500">
                      Account Required
                    </p>
                    <p className="text-sm text-gray-500 mb-4">
                      Sign up or log in to upload videos
                    </p>
                    <button
                      onClick={() => setShowAuthPrompt(true)}
                      className="bg-red-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-red-700 transition-colors"
                    >
                      Create Account
                    </button>
                  </div>
                ) : isDragActive ? (
                  <p className="text-xl font-medium animate-fade-in">Drop it here...</p>
                ) : (
                  <div>
                    <p className="text-xl font-medium mb-2">
                      Drag & drop your video{isPro ? 's' : ''}
                    </p>
                    <p className="text-sm text-primary-600 mb-4">
                      or click to browse
                    </p>
                    <p className="text-xs text-primary-500">
                      MP4, AVI, MKV, MOV, WMV, FLV, WebM (max {isPro ? '2GB' : '500MB'} each)
                      {isPro && <span className="block mt-1">Pro: Upload up to 10 files</span>}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Selected Files */}
            <div>
              <div className="card">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-display font-semibold">
                    Selected Files ({selectedFiles.length})
                  </h2>
                  <button
                    onClick={resetUpload}
                    className="btn-ghost text-sm"
                  >
                    Clear All
                  </button>
                </div>
                
                <div className="space-y-3 mb-6">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                          <File className="h-5 w-5 text-primary-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{file.name}</p>
                          <p className="text-sm text-gray-600">{formatFileSize(file.size)}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => removeFile(index)}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors rounded-lg hover:bg-white"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
                
                {isPro && selectedFiles.length < 10 && (
                  <div
                    {...getRootProps()}
                    className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-primary-400 hover:bg-gray-50 transition-colors"
                  >
                    <input {...getInputProps()} />
                    <Plus className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm text-gray-600">
                      Add more files ({selectedFiles.length}/{10})
                    </p>
                  </div>
                )}
                
                {/* Clip Generation Settings */}
                <div className="border-t border-gray-200 pt-6 mt-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                        <Settings className="h-5 w-5 text-primary-600" />
                      </div>
                      <div>
                        <h3 className="text-xl font-display font-semibold text-gray-900">Clip Generation Settings</h3>
                        <p className="text-sm text-primary-600">Configure how your video will be analyzed</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowSettings(!showSettings)}
                      className="flex items-center space-x-2 px-3 py-2 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-all duration-200"
                    >
                      <span>{showSettings ? 'Hide' : 'Show'}</span>
                      {showSettings ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  
                  {showSettings && (
                    <div className="space-y-8 bg-gradient-to-br from-gray-50 to-primary-50 rounded-xl p-8 border border-primary-100 animate-fade-up">
                      {/* Sensitivity */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <label className="block text-base font-semibold text-gray-900">
                            Detection Sensitivity
                          </label>
                          <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-medium">
                            {Math.round((analysisSettings.sensitivity || 0.3) * 100)}%
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">
                          Higher sensitivity finds more highlights but may include less exciting moments
                        </p>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.1"
                          value={analysisSettings.sensitivity}
                          onChange={(e) => setAnalysisSettings(prev => ({
                            ...prev,
                            sensitivity: parseFloat(e.target.value)
                          }))}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer 
                                   [&::-webkit-slider-thumb]:appearance-none 
                                   [&::-webkit-slider-thumb]:w-5 
                                   [&::-webkit-slider-thumb]:h-5 
                                   [&::-webkit-slider-thumb]:rounded-full 
                                   [&::-webkit-slider-thumb]:bg-red-500 
                                   [&::-webkit-slider-thumb]:cursor-pointer
                                   [&::-webkit-slider-thumb]:shadow-md
                                   [&::-webkit-slider-thumb]:border-2
                                   [&::-webkit-slider-thumb]:border-white
                                   [&::-webkit-slider-thumb]:hover:bg-red-600
                                   [&::-webkit-slider-thumb]:transition-colors
                                   [&::-moz-range-thumb]:w-5 
                                   [&::-moz-range-thumb]:h-5 
                                   [&::-moz-range-thumb]:rounded-full 
                                   [&::-moz-range-thumb]:bg-red-500 
                                   [&::-moz-range-thumb]:cursor-pointer
                                   [&::-moz-range-thumb]:border-2
                                   [&::-moz-range-thumb]:border-white
                                   [&::-moz-range-thumb]:shadow-md
                                   [&::-moz-range-track]:bg-gray-200
                                   [&::-moz-range-track]:rounded-lg
                                   [&::-moz-range-track]:h-2"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-2">
                          <span>Conservative</span>
                          <span>Balanced</span>
                          <span>Aggressive</span>
                        </div>
                      </div>

                      {/* Clip Duration */}
                      <div className="space-y-4">
                        <h4 className="text-base font-semibold text-gray-900">Clip Duration</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                              Minimum Duration (seconds)
                            </label>
                            <input
                              type="number"
                              min="5"
                              max="300"
                              value={analysisSettings.min_clip_duration}
                              onChange={(e) => setAnalysisSettings(prev => ({
                                ...prev,
                                min_clip_duration: parseInt(e.target.value) || 15
                              }))}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                              Maximum Duration (seconds)
                            </label>
                            <input
                              type="number"
                              min="10"
                              max="600"
                              value={analysisSettings.max_clip_duration}
                              onChange={(e) => setAnalysisSettings(prev => ({
                                ...prev,
                                max_clip_duration: parseInt(e.target.value) || 60
                              }))}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Padding */}
                      <div className="space-y-4">
                        <h4 className="text-base font-semibold text-gray-900">Clip Padding</h4>
                        <p className="text-sm text-gray-600">Add extra time before and after highlights for context</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                              Before Highlight (seconds)
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="30"
                              value={analysisSettings.padding_before}
                              onChange={(e) => setAnalysisSettings(prev => ({
                                ...prev,
                                padding_before: parseInt(e.target.value) || 3
                              }))}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                              After Highlight (seconds)
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="30"
                              value={analysisSettings.padding_after}
                              onChange={(e) => setAnalysisSettings(prev => ({
                                ...prev,
                                padding_after: parseInt(e.target.value) || 3
                              }))}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Analysis Features */}
                      <div className="space-y-4">
                        <h4 className="text-base font-semibold text-gray-900">Analysis Features</h4>
                        <p className="text-sm text-gray-600">Choose which types of moments to detect</p>
                        <div className="space-y-4">
                          <label className="flex items-start space-x-3 p-4 bg-white rounded-lg border border-gray-200 hover:border-gray-400 transition-colors cursor-pointer">
                            <input
                              type="checkbox"
                              checked={analysisSettings.enable_audio_analysis}
                              onChange={(e) => setAnalysisSettings(prev => ({
                                ...prev,
                                enable_audio_analysis: e.target.checked
                              }))}
                              className="mt-1 w-4 h-4 rounded border-2 border-gray-300 bg-white accent-gray-900
                                       focus:ring-2 focus:ring-red-500 focus:ring-offset-0 focus:border-red-500
                                       hover:border-gray-500 transition-colors cursor-pointer"
                            />
                            <div>
                              <span className="text-sm font-medium text-gray-900">
                                Audio Analysis
                              </span>
                              <p className="text-xs text-gray-600 mt-1">
                                Detect excitement, reactions, and vocal peaks in your voice
                              </p>
                            </div>
                          </label>
                          <label className="flex items-start space-x-3 p-4 bg-white rounded-lg border border-gray-200 hover:border-gray-400 transition-colors cursor-pointer">
                            <input
                              type="checkbox"
                              checked={analysisSettings.enable_face_detection}
                              onChange={(e) => setAnalysisSettings(prev => ({
                                ...prev,
                                enable_face_detection: e.target.checked
                              }))}
                              className="mt-1 w-4 h-4 rounded border-2 border-gray-300 bg-white accent-gray-900
                                       focus:ring-2 focus:ring-red-500 focus:ring-offset-0 focus:border-red-500
                                       hover:border-gray-500 transition-colors cursor-pointer"
                            />
                            <div>
                              <span className="text-sm font-medium text-gray-900">
                                Face Detection
                              </span>
                              <p className="text-xs text-gray-600 mt-1">
                                Identify moments with clear facial expressions and reactions
                              </p>
                            </div>
                          </label>
                          <label className="flex items-start space-x-3 p-4 bg-white rounded-lg border border-gray-200 hover:border-gray-400 transition-colors cursor-pointer">
                            <input
                              type="checkbox"
                              checked={analysisSettings.enable_emotion_detection}
                              onChange={(e) => setAnalysisSettings(prev => ({
                                ...prev,
                                enable_emotion_detection: e.target.checked
                              }))}
                              className="mt-1 w-4 h-4 rounded border-2 border-gray-300 bg-white accent-gray-900
                                       focus:ring-2 focus:ring-red-500 focus:ring-offset-0 focus:border-red-500
                                       hover:border-gray-500 transition-colors cursor-pointer"
                            />
                            <div>
                              <span className="text-sm font-medium text-gray-900">
                                Emotion Detection
                              </span>
                              <p className="text-xs text-gray-600 mt-1">
                                Analyze facial emotions for surprise, excitement, and engagement
                              </p>
                            </div>
                          </label>
                        </div>
                      </div>

                      {/* Video Conversion section removed - was confusing UX as users thought it converted their uploaded video but it only affected analysis */}

                      {/* Quick Presets */}
                      <div className="space-y-4">
                        <h4 className="text-base font-semibold text-gray-900">Quick Presets</h4>
                        <p className="text-sm text-gray-600">Apply optimized settings for different content types</p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {Object.entries(presets).map(([key, preset]) => {
                            const isSelected = selectedPreset === key;
                            return (
                              <button
                                key={key}
                                onClick={() => applyPreset(key)}
                                className={`p-4 bg-gradient-to-br rounded-xl transition-all duration-200 text-left group relative ${
                                  isSelected 
                                    ? 'from-primary-100 to-primary-200 border-2 border-primary-400 shadow-lg' 
                                    : 'from-white to-gray-50 border border-gray-200 hover:from-gray-50 hover:to-gray-100 hover:border-gray-300 hover:shadow-md'
                                }`}
                              >
                                {isSelected && (
                                  <div className="absolute top-2 right-2 w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
                                    <div className="w-3 h-3 bg-white rounded-full"></div>
                                  </div>
                                )}
                                <div className="flex items-center space-x-3 mb-3">
                                  <div className={`w-8 h-8 bg-gradient-to-br ${preset.color} rounded-lg flex items-center justify-center shadow-sm`}>
                                    <div className="w-3 h-3 bg-white rounded-full"></div>
                                  </div>
                                  <span className={`font-semibold ${isSelected ? 'text-primary-800' : 'text-gray-900'}`}>
                                    {preset.name}
                                  </span>
                                </div>
                                <p className={`text-sm mb-2 ${isSelected ? 'text-primary-700' : 'text-gray-600'}`}>
                                  {preset.description}
                                </p>
                                <p className={`text-xs font-medium ${isSelected ? 'text-primary-600' : preset.textColor}`}>
                                  {preset.subtitle}
                                </p>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Watermark Warning for Free Users */}
                {!isPro && selectedFiles.length > 0 && (
                  <div className="pt-4">
                    <WatermarkWarning 
                      onUpgrade={() => setShowSubscriptionModal(true)}
                    />
                  </div>
                )}
                
                <div className="flex justify-end pt-4">
                  <button
                    onClick={handleUploadAll}
                    disabled={selectedFiles.length === 0 || isUploading || isUrlImporting || tasks.some(task => task.status === 'uploading') || (!currentUser && !canUseGuestMode)}
                    className="btn-danger flex-1 flex items-center justify-center"
                  >
                    {isUploading || tasks.some(task => task.status === 'uploading') ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <UploadIcon className="h-5 w-5 mr-2" />
                        {!currentUser && canUseGuestMode
                          ? 'Try Free - Generate Clips'
                          : isPro 
                          ? `Upload ${selectedFiles.length} File${selectedFiles.length !== 1 ? 's' : ''}` 
                          : 'Upload Video'
                        }
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}


      </div>
      </div>
      </div>
      
      {/* Auth Prompt Modal */}
      {showAuthPrompt && (
        <AuthPrompt
          onClose={() => setShowAuthPrompt(false)}
          title="Sign Up to Upload Videos"
          message="Create a free account to start generating amazing clips from your videos"
        />
      )}

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
      />
      
      {/* Guest Limit Reached Modal */}
      {showGuestLimitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <GuestLimitReached 
            feature="upload" 
            onClose={() => setShowGuestLimitModal(false)}
          />
        </div>
      )}
      
      {/* Exit Intent Modal */}
      <ExitIntentModal
        isOpen={showExitIntent}
        onClose={dismissExitIntent}
        userType={currentUser ? 'free' : 'guest'}
      />
    </>
  );
};

export default Upload;